import { useEffect } from 'react';
import { directionFor, LanguageCode } from '../utils/i18n';

interface SEOProps {
  title: string;
  description?: string;
  canonical?: string;
  type?: string;
  name?: string;
  /**
   * Marks the page as noindex,follow. Use for pages that exist but should never
   * be a search result — not-found states and embeddable widget frames. Without
   * it those URLs are indexable copies of a page that says nothing, which is
   * what Search Console flags as "Crawled - currently not indexed".
   */
  noindex?: boolean;
  /** BCP 47 code for <html lang>. */
  lang?: string;
  /**
   * Language alternates for this page, as code -> path. Emitted as hreflang
   * links plus x-default, which is what tells Google the Spanish page is a
   * translation of the English one rather than a competing duplicate.
   */
  alternates?: Record<string, string>;
}

export default function SEO({
  title,
  description = "Professional unit converter for length, weight, temperature, area, volume, time, speed, and energy. Get instant, accurate conversions with a beautiful interface.",
  canonical,
  type = 'website',
  name = 'Simple Unit Converter',
  noindex = false,
  lang = 'en',
  alternates
}: SEOProps) {
  const siteUrl = 'https://simpleunitconverter.co.uk';
  // No canonical prop means the page has no canonical URL to claim. Falling back
  // to the site root would have every such page nominate the homepage as its
  // canonical, which is never what the caller meant.
  const fullCanonical = canonical ? `${siteUrl}${canonical}` : null;

  useEffect(() => {
    // Update Title
    document.title = title;

    // Helper to update or create meta tags
    const updateMeta = (selector: string | null, tagName: string, attributeName: string, attributeValue: string, content: string) => {
      let element = selector ? document.querySelector(selector) : null;
      if (!element) {
        element = document.createElement(tagName);
        element.setAttribute(attributeName, attributeValue);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Helper to update, create or drop link tags (canonical)
    const setLink = (rel: string, href: string | null) => {
      const existing = document.querySelector(`link[rel="${rel}"]`);
      if (!href) {
        // The prerendered shell for a different route may still be in <head>;
        // leaving it would point this page at an unrelated canonical.
        existing?.remove();
        return;
      }
      const element = existing ?? document.head.appendChild(
        Object.assign(document.createElement('link'), { rel })
      );
      element.setAttribute('href', href);
    };

    // Update Standard Meta Tags
    updateMeta(`meta[name="description"]`, 'meta', 'name', 'description', description);

    // Robots directive. Removed rather than set to "index, follow" when the page
    // is indexable, so the absence of the tag is the default.
    const robots = document.querySelector('meta[name="robots"]');
    if (noindex) {
      updateMeta(`meta[name="robots"]`, 'meta', 'name', 'robots', 'noindex, follow');
    } else {
      robots?.remove();
    }

    // Update Open Graph Tags
    updateMeta(`meta[property="og:type"]`, 'meta', 'property', 'og:type', type);
    updateMeta(`meta[property="og:title"]`, 'meta', 'property', 'og:title', title);
    updateMeta(`meta[property="og:description"]`, 'meta', 'property', 'og:description', description);
    if (fullCanonical) {
      updateMeta(`meta[property="og:url"]`, 'meta', 'property', 'og:url', fullCanonical);
    }

    // Update Twitter Tags
    updateMeta(`meta[name="twitter:creator"]`, 'meta', 'name', 'twitter:creator', name);
    updateMeta(`meta[name="twitter:card"]`, 'meta', 'name', 'twitter:card', type === 'article' ? 'summary_large_image' : 'summary');
    updateMeta(`meta[name="twitter:title"]`, 'meta', 'name', 'twitter:title', title);
    updateMeta(`meta[name="twitter:description"]`, 'meta', 'name', 'twitter:description', description);

    // Update Canonical Link
    setLink('canonical', fullCanonical);

    document.documentElement.lang = lang;
    // Arabic is right to left. Setting dir on <html> mirrors the whole layout,
    // including the flex rows and the text alignment Tailwind emits, without
    // every component having to know which language it is in.
    document.documentElement.dir = directionFor(lang as LanguageCode);

    // Rebuild the hreflang set each render: a stale alternate from the previous
    // page would claim this URL translates something it does not.
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
    if (alternates) {
      const head = document.head;
      for (const [code, path] of Object.entries(alternates)) {
        const el = document.createElement('link');
        el.setAttribute('rel', 'alternate');
        el.setAttribute('hreflang', code);
        el.setAttribute('href', `${siteUrl}${path}`);
        head.appendChild(el);
      }
      if (alternates.en) {
        const def = document.createElement('link');
        def.setAttribute('rel', 'alternate');
        def.setAttribute('hreflang', 'x-default');
        def.setAttribute('href', `${siteUrl}${alternates.en}`);
        head.appendChild(def);
      }
    }

  }, [title, description, fullCanonical, type, name, noindex, lang]);

  return null;
}
